import { format } from 'date-fns';
import { AlertTriangle, Clock, PackageCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SAMPLE_PROBLEM_LABEL, type SampleReceipt } from '@/lib/sample-receipt';

/** Creator-reported sample receipt as a compact table badge. A problem (or a
 *  received sample that followed one) shows the creator's report on hover;
 *  resolving it happens on the hire detail page (SampleReceiptCard). */
export function SampleReceiptBadge({ receipt, live }: {
  receipt: SampleReceipt;
  /**
   * Whether the creator app is STILL capable of asking this hire's creator
   * the sample question right now — true only for `status === 'confirmed'`
   * with no `settled_at`, the same gate the creator app itself uses to show
   * the prompt at all. A finished (completed/settled) hire will never be
   * asked again, so an unanswered one there isn't "awaiting" anything — it's
   * just information that was never collected. Ignored once there IS an
   * answer: a receipt/problem already on record stays visible regardless of
   * what the hire does next.
   */
  live: boolean;
}) {
  const status = receipt.sample_receipt_status;

  if (!status) {
    // Finished without an answer — nothing left to wait on, so a plain dash
    // reads more honestly than a stale "awaiting" badge would.
    if (!live) return <span className="text-muted-foreground text-xs">—</span>;
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="text-muted-foreground gap-1 font-normal cursor-help">
              <Clock className="h-3 w-3" /> Creator: awaiting
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64 text-xs">
            The creator hasn't confirmed whether their sample arrived yet. They're asked in the app while this hire is active. Open the hire to record their answer if they told support.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const autoDetected = receipt.sample_receipt_source === 'auto_video_submission';
  const badge = status === 'received' ? (
    <Badge
      className="bg-green-100 text-green-800 border border-green-200 gap-1"
      title={autoDetected ? 'Auto-detected — the creator submitted a video before answering the sample question' : undefined}
    >
      <PackageCheck className="h-3 w-3" /> {autoDetected ? 'Auto: received' : receipt.sample_receipt_source === 'admin' ? 'BMC: received' : 'Creator: received'}
    </Badge>
  ) : (
    <Badge className="bg-amber-100 text-amber-800 border border-amber-200 gap-1 cursor-help">
      <AlertTriangle className="h-3 w-3" /> {SAMPLE_PROBLEM_LABEL[receipt.sample_problem_type ?? 'other']}
    </Badge>
  );

  if (!receipt.sample_problem_reported_at) return badge;

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild><span className="inline-flex">{badge}</span></HoverCardTrigger>
      <HoverCardContent className="w-72 text-sm space-y-2">
        <div className="flex items-center gap-1.5 font-semibold">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          {SAMPLE_PROBLEM_LABEL[receipt.sample_problem_type ?? 'other']}
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            {receipt.sample_problem_reported_by === 'admin' ? 'BMC · ' : ''}{format(new Date(receipt.sample_problem_reported_at), 'MMM d')}
          </span>
        </div>
        {receipt.sample_problem_note
          ? <p className="rounded-md border-l-4 border-amber-400 bg-amber-50/70 dark:bg-amber-500/10 px-3 py-2 italic whitespace-pre-wrap break-words">{receipt.sample_problem_note}</p>
          : <p className="text-muted-foreground italic">No details given.</p>}
        {status === 'received' && receipt.sample_problem_resolved_at && (
          <p className="text-xs text-green-700">
            Resolved {format(new Date(receipt.sample_problem_resolved_at), 'MMM d')} —{' '}
            {receipt.sample_problem_resolved_by === 'admin' ? 'closed by BMC'
              : receipt.sample_problem_resolved_by === 'auto_video_submission' ? 'auto-resolved by a video submission'
              : 'creator confirmed it arrived'}
          </p>
        )}
        {status === 'problem' && <p className="text-xs text-muted-foreground">Open the hire to resolve it.</p>}
      </HoverCardContent>
    </HoverCard>
  );
}
